import classes from "./FormChoosePermits.module.css";
import FormChoosePermitsOption from "./FormChoosePermitsOption";
import FormChoosePermitsComplete from "./FormChoosePermitsComplete";
import { useCurrentDataContext } from "../../../contexts/CurrentDataContext";
import SaveUnfinishedButton from "../Buttons/SaveUnfinishedButton/SaveUnfinishedButton";
import { useTranslation } from "react-i18next";
import FormNotActive from "../FormNotActive/FormNotActive";
import { useEffect, useState } from "react";
import { getSelectablePermits } from "../../../scripts/dataHandlers";
import { useUserContext } from "../../../contexts/UserContext";
import {
  Employee,
  PermitFormBody,
  SearchLabel,
  SelectablePermit,
} from "../../../contexts/interfaces";
import { BsPlusCircle } from "react-icons/bs";
import { useErrorContext } from "../../../contexts/ErrorContext";
import Select from "react-select";
import { GrCircleInformation } from "react-icons/gr";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

interface Props {
  formSubmitHandler: (
    e: React.SyntheticEvent<HTMLFormElement> | undefined,
    step: string,
    unfinished?: string | undefined
  ) => void;
  setLoading: (val: boolean) => void;
  chosenEmployee: Employee;
}

const FormChoosePermits = ({
  formSubmitHandler,
  setLoading,
  chosenEmployee,
}: Props) => {
  const [options, setOptions] = useState<SelectablePermit[]>();

  const [otherPermitInput, setOtherPermitInput] = useState<string>("");
  // Set translation
  const { t } = useTranslation();

  const searchOptions: SearchLabel[] = chosenEmployee?.permitsValidUntil !== null
    ? [
        {
          label: t("formChoosePermits.replacePreviousPermitsTrue"),
          value: "true",
        },
        {
          label: t("formChoosePermits.replacePreviousPermitsFalse"),
          value: "false",
        },
      ]
    : [
        {
          label: t("formChoosePermits.replacePreviousPermitsTrue"),
          value: "true",
        },
      ];

  const { currentData, setCurrentData } = useCurrentDataContext();

  const { user } = useUserContext();

  const { handleError } = useErrorContext();

  // Get list of selectable permits from backend.
  // Chosen educationLevel parameter defines what permits will be included in result
  useEffect(() => {
    const getSelectablePermitsFromBackend = async () => {
      const res =
        user?.id &&
        currentData?.educationId &&
        (await getSelectablePermits(user.id, currentData?.educationId));

      console.log("res: ", res);
      if (res?.status === 200 && currentData?.permits) {
        const resOptions: SelectablePermit[] = res.data.result;

        console.log("resOptions: ", resOptions);
        console.log(
          "currentData.permits before processing: ",
          JSON.stringify(currentData.permits)
        );
        setOptions([...resOptions]);

        // Compare selectable permits and data user has entered in formData.permits
        // If permit is no longer available (educationLevel has changed selectablePermits) and doesnt exist in selectablePermits,
        // set isChecked value to false. (So permit isnt applied for anymore, but data is still saved)
        let modifiedPermits = currentData.permits.map((permit) => {
          // If permitItemId is null or undefined, try to match by fullTitle
          if (
            permit.permitItemId === undefined ||
            permit.permitItemId === null
          ) {
            const matchingOption = resOptions?.find(
              (option) => option.fullTitle === permit.fullTitle
            );
            if (matchingOption) {
              return {
                ...permit,
                permitItemId: matchingOption.id,
              };
            }
          }

          // If selectablePermit options dont have any id matching permitItemId, set isChecked to false
          if (
            resOptions?.findIndex(
              (option) => option.id === permit.permitItemId
            ) === -1
          ) {
            return {
              ...permit,
              isChecked: false,
            };
          } else {
            return { ...permit };
          }
        });

        // Update currentData with modifiedPermits
        setCurrentData({
          ...currentData,
          permits: [...modifiedPermits],
        });
      } else {
        handleError(res, "GetSelectablePermitsFailed");
      }
    };
    currentData?.educationId &&
      currentData?.permits &&
      getSelectablePermitsFromBackend();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentData?.educationId]);

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    setLoading(true);
    // Call for form submitHandler, which updates data to database
    formSubmitHandler(e, "approval", undefined);
  };

  // Function which checks disabled status for submit button
  // If all checkboxes are unchecked, set disabled = true
  // is checked, it will disable submit button
  const checkDisabled = () => {
    let isDisabled;
    if (
      currentData?.permits?.every((permit) => !permit.isChecked) ||
      currentData?.replacePrevious === null
    ) {
      isDisabled = true;
    } else {
      isDisabled = false;
    }
    return isDisabled;
  };

  const addOtherPermit = () => {
    // TODO: Show error about empty inputfield?
    // If inputfield isnt empty and permit array exists
    if (otherPermitInput !== "" && currentData?.permits) {
      let newPermits: PermitFormBody[] = [
        ...currentData.permits,
        {
          completedAt: null,
          demonstrationAssessor1: null,
          demonstrationAssessor1Date: null,
          demonstrationAssessor2: null,
          demonstrationAssessor2Date: null,
          demonstrationAssessor3: null,
          demonstrationAssessor3Date: null,
          employeeId: null,
          fullTitle: otherPermitInput,
          isChecked: true,
          otherMedicineContent: otherPermitInput,
          permitId:
            +Date.now().toString().slice(-8) + Math.floor(Math.random() * 10),
          permitItemId: 9,
          title: "Muu",
          type: "medicineOther",
          validUntil: null,
        },
      ];

      setCurrentData({
        ...currentData,
        permits: [...newPermits],
      });
    }
  };

  if (currentData?.formStatus === "permits" && currentData.permits) {
    // Filter currentData.permits and get array of permits that are also found in selectablePermits
    // (Dont show permits that might have data, but arent currently visible because user changed educationLevel afterwards)
    const filteredPermits = currentData.permits.filter((permit) => {
      // If permitItemId is missing, we can't filter - should have been fixed in useEffect
      if (permit.permitItemId === undefined || permit.permitItemId === null) {
        return false;
      }

      const hasMatching = options?.some((option) => {
        const matches = option.id === permit.permitItemId;
        return matches;
      });
      return hasMatching;
    });


    // Log any permits that have permitItemId undefined or null
    const invalidPermits = currentData.permits.filter(
      (p) => p.permitItemId === undefined || p.permitItemId === null
    );
    if (invalidPermits.length > 0) {
      console.log("Found permits with invalid permitItemId:", invalidPermits);
    }

    // CustomStyles will add cursor:pointer to select menu and options
    const customStyles = {
      control: (provided: any) => ({
        // ,state: any
        ...provided,
        cursor: "pointer",
      }),
      option: (provided: any) => ({
        ...provided,
        cursor: "pointer",
      }),
    };

    const checkDefaultValue = () => {
      // If replacePrevious selection is true OR employee has no
      // previous permits at all, show first option (these permits will replace any previous ones)
      if (
        (currentData?.replacePrevious !== null &&
          currentData?.replacePrevious === true) ||
        chosenEmployee?.permitsValidUntil === null
      ) {
        return searchOptions[0];
        // Else if replacePrevious is false
      } else if (
        currentData?.replacePrevious !== null &&
        currentData?.replacePrevious === false
      ) {
        return searchOptions[1];
      } else {
        // Otherwise show placeholder text for selection (user hasnt selected anything yet)
        return null;
      }
    };

    return (
      <div className={classes.formChoosePermitsContainer}>
        <Tooltip id="forms-tooltip" float style={{ zIndex: 9999 }} />
        <div className={classes.formChoosePermitsTitle}>
          2 {String(t("formChoosePermits.mainTitle"))}
        </div>
        <form
          className={classes.formChoosePermitsInnerContainer}
          onSubmit={(e) => handleSubmit(e)}
        >
          <div className={classes.formChoosePermitsTitle}>
            {t("formChoosePermits.requestedPermits")}
          </div>
          <div className={classes.replacePreviousRow}>
            <Select
              defaultValue={checkDefaultValue()}
              isSearchable={false}
              className={classes.replacePreviousSelect}
              options={searchOptions}
              styles={customStyles}
              placeholder={t("formChoosePermits.replacePreviousPlaceholder")}
              onChange={(e) =>
                e &&
                currentData &&
                setCurrentData({
                  ...currentData,
                  replacePrevious: e.value === "true" ? true : false,
                })
              }
              isClearable={false}
              // components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
            />
            {currentData.replacePrevious !== null && (
              <GrCircleInformation
                className={classes.infoBubble}
                data-tooltip-id="forms-tooltip"
                data-tooltip-content={String(
                  currentData.replacePrevious
                    ? t(
                        "formChoosePermits.replacePreviousPermitsTrueTooltipText"
                      )
                    : t(
                        "formChoosePermits.replacePreviousPermitsFalseTooltipText"
                      )
                )}
              />
            )}
          </div>

          <div className={classes.formChoosePermitsCheckboxContainer}>
            {
              // Map filteredPermits, showing clickable permit item
              filteredPermits.map((item, i) => (
                <div key={"perm" + i}>
                  <FormChoosePermitsOption
                    id={item.permitId}
                    fullTitle={item.fullTitle}
                  />
                </div>
              ))
            }
            {currentData?.educationId !== 7 && (
              <div className={classes.otherPermitRowContainer}>
                <BsPlusCircle
                  className={classes.icon}
                  type="button"
                  onClick={() => {
                    addOtherPermit();
                    setOtherPermitInput("");
                  }}
                />

                <input
                  className={classes.otherPermitInput}
                  type={"text"}
                  placeholder={"Nimeä ja lisää muu lääkelupa"}
                  value={otherPermitInput}
                  onChange={(e) => setOtherPermitInput(e.target.value)}
                />
              </div>
            )}
          </div>

          <button
            className="formContinueButton"
            type="submit"
            disabled={checkDisabled()}
          >
            {t("formChoosePermits.continueButton")}
          </button>

          <SaveUnfinishedButton
            formSubmitHandler={formSubmitHandler}
            step={"permits"}
          />
        </form>
      </div>
    );
  } else if (currentData?.permits?.every((permit) => !permit.isChecked)) {
    return (
      <FormNotActive
        title={`2 ${t("formChoosePermits.mainTitle")}`}
        text={`${t("formChoosePermits.noSelections")}`}
      />
    );
  } else {
    return (
      <FormChoosePermitsComplete
        formSubmitHandler={formSubmitHandler}
        setLoading={setLoading}
      />
    );
  }
};

export default FormChoosePermits;
